import { Flex, Input, Dropdown, Button, Divider } from '@components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { registerInput } from '@utils/reactHookForm';
import { getErrorProps } from '@utils/validation/validationFunctions';
import { ultimateBeneficialOwnerFormSchema } from '@validationSchemas/investment';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useEffect } from 'react';
import {
  useCreateUBOMutation,
  useUpdateUBOMutation,
  useDeleteUBOMutation,
} from '@features/api/ultimateBeneficialOwnerSlice';
import { UltimateBeneficialOwner } from '@features/api/response/UltimateBeneficialOwner';
import UltimateBeneficialOwnerSummary from './UltimateBeneficialOwnerSummary';
import { UltimateBeneficialOwnerCompanyPosition } from '@features/api/request/UltimateBeneficialOwner';

const UltimateBeneficialOwnerForm: React.FC<{
  isActive: boolean;
  ultimateBeneficialOwner?: Partial<UltimateBeneficialOwner>;
  onSave: () => void;
  onDelete: () => void;
  onRemove: () => void;
  onEdit: () => void;
  canRemove: boolean;
  showDivider: boolean;
}> = ({
  ultimateBeneficialOwner,
  isActive,
  onSave,
  onDelete,
  onEdit,
  canRemove,
  onRemove,
  showDivider,
}) => {
  const intl = useIntl();

  const [createUBOMutation] = useCreateUBOMutation();
  const [updateUBOMutation] = useUpdateUBOMutation();
  const [deleteUBOMutation] = useDeleteUBOMutation();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset,
  } = useForm<UltimateBeneficialOwner>({
    resolver: yupResolver(ultimateBeneficialOwnerFormSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: ultimateBeneficialOwner
      ? { ...ultimateBeneficialOwner }
      : {},
    shouldUnregister: true,
  });

  useEffect(() => {
    reset(ultimateBeneficialOwner ? { ...ultimateBeneficialOwner } : {});
  }, [reset, ultimateBeneficialOwner]);

  const handleSave = async (ubo: UltimateBeneficialOwner) => {
    if (ultimateBeneficialOwner?.id) {
      await updateUBOMutation({
        ...ubo,
        id: ultimateBeneficialOwner?.id,
      }).unwrap();
    } else {
      const { id, ...rest } = ubo;
      await createUBOMutation({ ...rest }).unwrap();
    }

    onSave();
  };

  const handleRemoveClick = async () => {
    if (ultimateBeneficialOwner?.id) {
      await deleteUBOMutation(ultimateBeneficialOwner.id).unwrap();
      onDelete();
      reset({});
    } else {
      onRemove();
    }
  };

  const handleEditClick = async () => {
    if (ultimateBeneficialOwner?.id) {
      onEdit();
    }
  };

  if (!isActive && ultimateBeneficialOwner?.id) {
    return (
      <UltimateBeneficialOwnerSummary
        ultimateBeneficialOwner={ultimateBeneficialOwner}
        handleEditClick={handleEditClick}
        handleRemoveClick={handleRemoveClick}
      />
    );
  }

  return (
    <form>
      {showDivider ? <Divider /> : null}

      <Flex mt={showDivider ? 12 : 0} gap={13} flexDirection='column'>
        <Flex gap={6}>
          <Input
            label={intl.formatMessage({
              id: 'investment.ubo.firstName',
            })}
            inputProps={{
              sx: {
                textTransform: 'capitalize',
              },
            }}
            {...registerInput(register, 'firstName')}
            {...getErrorProps(intl, errors.firstName)}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.ubo.middleName',
            })}
            inputProps={{
              sx: {
                textTransform: 'capitalize',
              },
            }}
            {...registerInput(register, 'middleName')}
            {...getErrorProps(intl, errors.middleName)}
          />
          <Input
            label={intl.formatMessage({
              id: 'investment.ubo.lastName',
            })}
            inputProps={{
              sx: {
                textTransform: 'capitalize',
              },
            }}
            {...registerInput(register, 'lastName')}
            {...getErrorProps(intl, errors.lastName)}
          />
        </Flex>
        <Flex gap={6}>
          <Box flex={1}>
            <Controller
              control={control}
              name='position'
              render={({ field }) => (
                <Dropdown
                  label={intl.formatMessage({
                    id: 'investment.ubo.companyPosition',
                  })}
                  menuItemData={[
                    {
                      value: UltimateBeneficialOwnerCompanyPosition.Shareholder,
                      name: intl.formatMessage({
                        id: 'investment.ubo.shareholder',
                      }),
                    },
                    {
                      value: UltimateBeneficialOwnerCompanyPosition.Director,
                      name: intl.formatMessage({
                        id: 'investment.ubo.director',
                      }),
                    },
                  ]}
                  {...field}
                />
              )}
            />
          </Box>
          <Flex flex={1} flexDirection='column'>
            <Input
              label={intl.formatMessage({
                id: 'investment.ubo.ownershipPercentage',
              })}
              InputProps={{
                type: 'number',
                inputProps: {
                  max: 100,
                  min: 25,
                },
              }}
              {...registerInput(register, 'ownershipPercentage')}
              {...getErrorProps(intl, errors.ownershipPercentage)}
            />
          </Flex>
        </Flex>
        <Input
          label={intl.formatMessage({
            id: 'investment.ubo.email',
          })}
          {...registerInput(register, 'email')}
          {...getErrorProps(intl, errors.email)}
        />
      </Flex>
      <Flex mt={13} flexDirection='column'>
        <Flex gap={4}>
          <Button onClick={handleSubmit(handleSave)} disabled={!isValid}>
            <FormattedMessage
              id={
                ultimateBeneficialOwner?.id
                  ? 'misc.save'
                  : 'investment.ubo.addUbo'
              }
            />
          </Button>
          {canRemove ? (
            <Button
              variant='tertiary'
              color='error'
              onClick={handleRemoveClick}
            >
              <FormattedMessage id='investment.ubo.remove' />
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </form>
  );
};

export default UltimateBeneficialOwnerForm;
